import SearchOutlined from '@ant-design/icons/SearchOutlined';
import Collapse from 'antd/es/collapse';
import Input from 'antd/es/input';
import React, { useMemo, useState } from 'react';
import { styled } from 'styled-components';

// import { getSupportedBlockTypes } from '../../constants/nodeTypes';
import { type NodeCategory } from '../nodes/BaseNode';

export interface BlockDefinition {
  type: string;
  name: string;
  description: string;
  category: NodeCategory;
  icon?: React.ReactNode;
  defaultConfig: Record<string, unknown>;
}

const BlockPaletteContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: transparent;
`;

const SearchContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  background: ${({ theme }) => theme.colorBgContainer};
  padding-bottom: ${({ theme }) => theme.marginSM}px;
  margin-bottom: ${({ theme }) => theme.marginSM}px;
  border-bottom: 1px solid ${({ theme }) => theme.colorBorderSecondary};
`;

const CategoriesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const CategoryCollapse = styled(Collapse)`
  .ant-collapse-header {
    padding: ${({ theme }) => theme.paddingXS}px
      ${({ theme }) => theme.paddingSM}px !important;
    font-size: 12px;
    font-weight: 600;
  }

  .ant-collapse-content-box {
    padding: ${({ theme }) => theme.paddingXS}px !important;
  }
`;

const BlockGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.marginXS}px;
  grid-template-columns: 1fr;
`;

const DraggableBlock = styled.div<{ category: NodeCategory }>`
  padding: ${({ theme }) => theme.paddingXS}px
    ${({ theme }) => theme.paddingSM}px;
  border-radius: ${({ theme }) => theme.borderRadiusSM}px;
  border: 1px solid ${({ theme }) => theme.colorBorder};
  background: ${({ theme }) => theme.colorBgContainer};
  cursor: grab;
  user-select: none;
  transition: all ${({ theme }) => theme.motionDurationFast}ms;

  &:hover {
    border-color: ${({ theme }) => theme.colorPrimary};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  &:active {
    cursor: grabbing;
    transform: translateY(0);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
  }
`;

const BlockName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colorText};
  margin-bottom: 2px;
  line-height: 1.2;
`;

const BlockDescription = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colorTextSecondary};
  line-height: 1.3;
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.paddingLG}px;
  text-align: center;
  color: ${({ theme }) => theme.colorTextSecondary};
  font-size: 12px;
`;

// Block definitions with metadata
const BLOCK_DEFINITIONS: BlockDefinition[] = [
  // IO Blocks
  {
    type: 'input',
    name: 'Input',
    description: 'Pipeline input with JSON schema',
    category: 'io',
    defaultConfig: {
      json_schema: {
        type: 'object',
        properties: {
          data: { type: 'string' },
        },
      },
    },
  },
  {
    type: 'output',
    name: 'Output',
    description: 'Pipeline output destination',
    category: 'io',
    defaultConfig: {
      name: 'output',
    },
  },
  {
    type: 'constant',
    name: 'Constant',
    description: 'Static value or configuration',
    category: 'io',
    defaultConfig: {
      config_value: '',
    },
  },
  {
    type: 'dataset',
    name: 'Dataset',
    description: 'Reference to existing dataset',
    category: 'io',
    defaultConfig: {
      dataset_id: '',
    },
  },
  {
    type: 'file',
    name: 'File',
    description: 'Reference to uploaded file',
    category: 'io',
    defaultConfig: {
      file_id: '',
    },
  },
  {
    type: 'artifact',
    name: 'Artifact',
    description: 'Store internal artifacts for debugging',
    category: 'io',
    defaultConfig: {
      name: 'artifact',
      include_metadata: true,
    },
  },

  // Flow Blocks
  {
    type: 'identity',
    name: 'Identity',
    description: 'Pass through data unchanged',
    category: 'flow',
    defaultConfig: {},
  },
  {
    type: 'zip',
    name: 'Zip',
    description: 'Combine multiple inputs',
    category: 'flow',
    defaultConfig: {
      combine_strategy: 'merge_objects',
    },
  },
  {
    type: 'map',
    name: 'Map',
    description: 'Apply block to each item',
    category: 'flow',
    defaultConfig: {
      target_block_type: '',
      target_config: {},
      max_concurrency: 5,
    },
  },
  {
    type: 'join',
    name: 'Join',
    description: 'SQL-style data joining',
    category: 'flow',
    defaultConfig: {
      base_dataset: '',
      joins: [],
      output_columns: [],
    },
  },

  // AI Blocks
  {
    type: 'llm',
    name: 'LLM',
    description: 'Language model inference',
    category: 'processing',
    defaultConfig: {
      provider: 'openai',
      model: 'small',
      temperature: 0.2,
      max_tokens: 8192,
    },
  },
  {
    type: 'embeddings',
    name: 'Embeddings',
    description: 'Generate text embeddings',
    category: 'processing',
    defaultConfig: {
      model: 'sentence-transformers/static-retrieval-mrl-en-v1',
      batch_size: 100,
      normalize: true,
    },
  },
  {
    type: 'semantic_filter',
    name: 'Semantic Filter',
    description: 'Filter by semantic similarity',
    category: 'processing',
    defaultConfig: {
      embedding_model: 'sentence-transformers/static-retrieval-mrl-en-v1',
      filter_strategy: 'cosine_similarity_top_k',
      top_k: 10,
      similarity_threshold: 0,
    },
  },
  {
    type: 'clause_extraction_agent',
    name: 'Clause Extraction Agent',
    description: 'Extract and analyze clauses from PDF documents',
    category: 'processing',
    defaultConfig: {
      instructions: 'Search for relevant clauses in the document',
    },
  },
  {
    type: 'legal_variance_determination',
    name: 'Legal Variance Determination',
    description: 'Analyze legal clauses against benchmarks to determine material variances',
    category: 'processing',
    defaultConfig: {
      instructions: 'Analyze extracted clauses for material variances',
    },
  },

  // Transform Blocks
  {
    type: 'string_template',
    name: 'String Template',
    description: 'Template-based text generation',
    category: 'transform',
    defaultConfig: {
      template: '',
      strip_whitespace: true,
    },
  },
  {
    type: 'text_splitter',
    name: 'Text Splitter',
    description: 'Split text into chunks',
    category: 'transform',
    defaultConfig: {
      chunk_size: 1000,
      chunk_overlap: 200,
    },
  },
  {
    type: 'transform',
    name: 'Transform',
    description: 'Apply data transformations',
    category: 'transform',
    defaultConfig: {
      operations: [],
    },
  },
  {
    type: 'pdf_reader',
    name: 'PDF Reader',
    description: 'Extract text from PDF files',
    category: 'document',
    defaultConfig: {},
  },
  {
    type: 'json_template',
    name: 'JSON Template',
    description: 'Render Jinja2 templates to generate structured JSON objects',
    category: 'transform',
    defaultConfig: {
      template: {},
    },
  },
];

// Category metadata
const CATEGORIES: Record<NodeCategory, { name: string; description: string }> =
  {
    io: {
      name: 'Input/Output',
      description: 'Data sources and destinations',
    },
    flow: {
      name: 'Flow Control',
      description: 'Control data flow and routing',
    },
    processing: {
      name: 'AI/ML Processing',
      description: 'AI and machine learning operations',
    },
    transform: {
      name: 'Data Transform',
      description: 'Data manipulation and processing',
    },
    document: {
      name: 'Document Processing',
      description: 'File and document operations',
    },
  };

export interface BlockPaletteProps {
  onBlockDragStart?: (block: BlockDefinition, event: React.DragEvent) => void;
  onBlockClick?: (block: BlockDefinition) => void;
}

export const BlockPalette: React.FC<BlockPaletteProps> = ({
  onBlockDragStart,
  onBlockClick,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategories, setActiveCategories] = useState<string[]>([
    'io',
    'flow',
    'processing',
    'transform',
    'document',
  ]);

  // Filter blocks based on search query
  const filteredBlocks = useMemo(() => {
    if (!searchQuery.trim()) {
      return BLOCK_DEFINITIONS;
    }

    const query = searchQuery.toLowerCase();
    return BLOCK_DEFINITIONS.filter(
      (block) =>
        block.name.toLowerCase().includes(query) ||
        block.description.toLowerCase().includes(query) ||
        block.type.toLowerCase().includes(query),
    );
  }, [searchQuery]);

  // Group blocks by category
  const blocksByCategory = useMemo(() => {
    const grouped: Record<NodeCategory, BlockDefinition[]> = {
      io: [],
      flow: [],
      processing: [],
      transform: [],
      document: [],
    };

    filteredBlocks.forEach((block) => {
      grouped[block.category].push(block);
    });

    return grouped;
  }, [filteredBlocks]);

  // Handle drag start
  const handleDragStart = (block: BlockDefinition, event: React.DragEvent) => {
    // Set drag data for drop handling
    event.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        type: 'block',
        blockType: block.type,
        blockDefinition: block,
      }),
    );

    event.dataTransfer.effectAllowed = 'copy';

    // Call parent handler
    onBlockDragStart?.(block, event);
  };

  // Handle block click (alternative to drag and drop)
  const handleBlockClick = (block: BlockDefinition) => {
    onBlockClick?.(block);
  };

  // Generate collapse items
  const collapseItems = Object.entries(CATEGORIES)
    .map(([categoryKey, category]) => {
      const categoryBlocks = blocksByCategory[categoryKey as NodeCategory];

      if (categoryBlocks.length === 0 && searchQuery.trim()) {
        return null; // Hide empty categories when searching
      }

      return {
        key: categoryKey,
        label: `${category.name} (${categoryBlocks.length})`,
        children: (
          <BlockGrid>
            {categoryBlocks.length === 0 ? (
              <EmptyState>No blocks found</EmptyState>
            ) : (
              categoryBlocks.map((block) => (
                <DraggableBlock
                  key={block.type}
                  category={block.category}
                  draggable
                  onDragStart={(event) => handleDragStart(block, event)}
                  onClick={() => handleBlockClick(block)}
                  title={`${block.name}: ${block.description}`}
                >
                  <BlockName>{block.name}</BlockName>
                  <BlockDescription>{block.description}</BlockDescription>
                </DraggableBlock>
              ))
            )}
          </BlockGrid>
        ),
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return (
    <BlockPaletteContainer>
      <SearchContainer>
        <Input
          size="small"
          placeholder="Search blocks..."
          prefix={<SearchOutlined />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
        />
      </SearchContainer>

      <CategoriesContainer>
        <CategoryCollapse
          size="small"
          ghost
          activeKey={activeCategories}
          onChange={(keys) => setActiveCategories(keys as string[])}
          items={collapseItems}
        />
      </CategoriesContainer>
    </BlockPaletteContainer>
  );
};
